import type { ExecutionResult } from '~/modules/query/ai/types';

import { isCanonicalJsonSchema } from '~/lib/ai/jsonSchema';

// V1 JSON Schema subset: object root, flat properties, no nested/array schemas.
export type DigitalAssistantFieldType = 'string' | 'number' | 'integer' | 'boolean' | 'object';

interface DigitalAssistantSchemaPropertyBase {
  title?: string;
  description?: string;
}

export interface DigitalAssistantStringProperty extends DigitalAssistantSchemaPropertyBase {
  type: 'string';
  enum?: string[];
}

export interface DigitalAssistantNumberProperty extends DigitalAssistantSchemaPropertyBase {
  type: 'number' | 'integer';
  enum?: number[];
}

export interface DigitalAssistantBooleanProperty extends DigitalAssistantSchemaPropertyBase {
  type: 'boolean';
}

// The backend describes no nested shape for object fields — only that the value
// must be a plain JSON object.
export interface DigitalAssistantObjectProperty extends DigitalAssistantSchemaPropertyBase {
  type: 'object';
}

export type DigitalAssistantSchemaProperty =
  | DigitalAssistantStringProperty
  | DigitalAssistantNumberProperty
  | DigitalAssistantBooleanProperty
  | DigitalAssistantObjectProperty;

export interface DigitalAssistantInputSchema {
  type: 'object';
  properties: Record<string, DigitalAssistantSchemaProperty>;
  required?: string[];
  title?: string;
  description?: string;
}

export type DigitalAssistantFieldValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | undefined;

export type DigitalAssistantFormValues = Record<string, DigitalAssistantFieldValue>;

/** Keyed by property name; the value is the message shown under that field. */
export type DigitalAssistantFormErrors = Record<string, string>;

/** service-ai's ExecutionResult, aliased so the tester never drifts from it. */
export type DigitalAssistantTestResult = ExecutionResult;

const CANONICAL_PROPERTY_TYPES = new Set(['string', 'number', 'boolean', 'object']);

function isCanonicalPropertyType(
  value: unknown,
): value is 'boolean' | 'number' | 'object' | 'string' {
  return typeof value === 'string' && CANONICAL_PROPERTY_TYPES.has(value);
}

/**
 * Converts a stored canonical inputPayloadSchema into the shape the tester
 * renders a form from. Legacy/non-canonical schemas and malformed properties
 * are never guessed at: they become an empty schema (or the property is
 * dropped). This never throws.
 */
export function canonicalSchemaToTesterSchema(
  schema: unknown,
  title?: string,
): DigitalAssistantInputSchema {
  if (!isCanonicalJsonSchema(schema)) {
    return { type: 'object', properties: {} };
  }

  const properties: Record<string, DigitalAssistantSchemaProperty> = {};

  for (const [key, rawProperty] of Object.entries(schema.properties)) {
    if (typeof rawProperty !== 'object' || rawProperty === null) {
      continue;
    }

    const candidate = rawProperty as Record<string, unknown>;
    if (!isCanonicalPropertyType(candidate.type)) {
      continue;
    }

    properties[key] = {
      type: candidate.type,
      description: typeof candidate.description === 'string' ? candidate.description : undefined,
    } as DigitalAssistantSchemaProperty;
  }

  const required = Array.isArray(schema.required)
    ? schema.required.filter((key): key is string => typeof key === 'string' && key in properties)
    : [];

  return { type: 'object', title, properties, required };
}

export function buildInitialValues(
  schema: DigitalAssistantInputSchema,
): DigitalAssistantFormValues {
  const values: DigitalAssistantFormValues = {};

  for (const [name, property] of Object.entries(schema.properties)) {
    values[name] = property.type === 'boolean' ? false : undefined;
  }

  return values;
}

export function validateRequiredFields(
  schema: DigitalAssistantInputSchema,
  values: DigitalAssistantFormValues,
): DigitalAssistantFormErrors {
  const errors: DigitalAssistantFormErrors = {};

  for (const name of schema.required ?? []) {
    const property = schema.properties[name];
    if (!property) {
      continue;
    }

    const value = values[name];

    if (property.type === 'boolean') {
      continue;
    }

    if (value === undefined) {
      errors[name] = 'This field is required.';
      continue;
    }

    if (typeof value === 'string' && value.trim() === '') {
      errors[name] = 'This field is required.';
      continue;
    }

    if (property.type === 'number' || property.type === 'integer') {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        errors[name] = 'This field must be a valid number.';
        continue;
      }

      if (property.type === 'integer' && !Number.isInteger(value)) {
        errors[name] = 'This field must be an integer.';
      }
    }
  }

  return errors;
}

export function buildTestPayload(values: DigitalAssistantFormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  for (const [name, value] of Object.entries(values)) {
    if (value !== undefined) {
      payload[name] = value;
    }
  }

  return payload;
}

export interface ParsedObjectFieldInput {
  isValid: boolean;
  value: Record<string, unknown> | undefined;
}

/**
 * Parses the JSON typed into an object field. Empty text is a valid "no value"
 * state; required-ness is enforced by validateRequiredFields. Invalid JSON and
 * non-object JSON are reported invalid rather than coerced.
 */
export function parseObjectFieldInput(rawText: string): ParsedObjectFieldInput {
  if (rawText.trim() === '') {
    return { isValid: true, value: undefined };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    return { isValid: false, value: undefined };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { isValid: false, value: undefined };
  }

  return { isValid: true, value: parsed as Record<string, unknown> };
}

/**
 * Formats an ExecutionResult for display: a string output as-is, a non-string
 * output pretty-printed, and a missing output as a short status message.
 */
export function formatExecutionOutput(result: DigitalAssistantTestResult): string {
  if (result.output === undefined) {
    return result.success ? '(no output)' : '(execution failed)';
  }

  if (typeof result.output === 'string') {
    return result.output;
  }

  try {
    return JSON.stringify(result.output, null, 2);
  } catch {
    return String(result.output);
  }
}
